import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import type {
  AvailableNumber,
  RegistrationPrefill,
  RegistrationSubmission,
} from '@/modules/reviews/api/reviewSmsApi';

/** Mirrors backend modules/texting/texting.service.ts TextingSetup. */
export type NumberState =
  | 'none'
  | 'requested'
  | 'ordered'
  | 'loa_pending'
  | 'porting'
  | 'active'
  | 'failed';

export interface TextingSetup {
  number: {
    mode: 'practice_hosted' | 'new_local' | 'toll_free' | null;
    last4: string | null;
    masked: string | null;
    provisioningState: NumberState;
    provisioningError: string | null;
    requestedAt: string | null;
    /** PracticeNova connected it from the Command Center. */
    setupByPracticeNova: boolean;
    /** The number they asked for, when PracticeNova connected a different one. */
    requestedLast4: string | null;
  };
  registration: {
    path: '10dlc' | 'toll_free' | null;
    brandState: string;
    campaignState: string;
    approved: boolean;
    rejectionReason: string | null;
    submittedAt: string | null;
    lastCheckedAt: string | null;
  };
  registrationOpen: boolean;
  checkEveryHours: number;
}

export interface NumberRequest {
  mode: 'new_local' | 'practice_hosted';
  e164: string;
  forwardTo?: string | null;
}

const base = (locationId: string) => `/locations/${encodeURIComponent(locationId)}/texting`;
const unwrap = <T>(p: Promise<{ data: ApiResponse<T> }>): Promise<T> => p.then((r) => r.data.data);

export const textingApi = {
  get: (locationId: string) => unwrap(apiClient.get<ApiResponse<TextingSetup>>(base(locationId))),
  searchNumbers: (locationId: string, areaCode?: string) =>
    unwrap(
      apiClient.get<ApiResponse<AvailableNumber[]>>(`${base(locationId)}/available-numbers`, {
        params: areaCode ? { areaCode } : {},
      }),
    ),
  requestNumber: (locationId: string, body: NumberRequest) =>
    unwrap(apiClient.post<ApiResponse<TextingSetup>>(`${base(locationId)}/number-request`, body)),
  registrationPrefill: (locationId: string) =>
    unwrap(
      apiClient.get<ApiResponse<RegistrationPrefill>>(`${base(locationId)}/registration/prefill`),
    ),
  submitRegistration: (locationId: string, body: RegistrationSubmission) =>
    unwrap(apiClient.post<ApiResponse<TextingSetup>>(`${base(locationId)}/registration`, body)),
  checkNow: (locationId: string) =>
    unwrap(apiClient.post<ApiResponse<TextingSetup>>(`${base(locationId)}/check`)),
};
